/**
 * SKU表单 - 智能定价与成本计算（重构版v2）
 * 职责：协调定价相关的逻辑和UI组件
 * 目标：< 150行
 */

import React from 'react';
import { Row, Col, Spin, Divider, Form, Input } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { ProfitScenarioAnalysis } from './components/ProfitScenarioAnalysis';
import { PricingParametersCard } from './components/PricingFields/PricingParametersCard';
import { PricingResultsPanel } from './components/PricingFields/PricingResultsPanel';
import { usePricingLogic } from './hooks/usePricingLogic';
import { getChannelResultColumns } from './components/channelResultColumns';
import { OperationStrategySimulation } from './components/OperationStrategySimulation';
import { useParams } from 'react-router-dom';

interface SKUFormPricingFieldsProps {
    form?: any;
}

export const SKUFormPricingFields: React.FC<SKUFormPricingFieldsProps> = ({ form: propForm }) => {
    const { id } = useParams<{ id: string }>();
    const skuId = Number(id);
    const { form, state, data } = usePricingLogic(propForm);
    const price = Form.useWatch('price', form);
    const profitMargin = Form.useWatch('profit_margin', form);

    if (data.loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16, color: '#999' }}>加载定价数据...</div>
            </div>
        );
    }

    return (
        <div className="sku-pricing-fields">
            {/* 上方：参数配置和结果分析 */}
            <Row gutter={24}>
                {/* 左侧：定价参数配置 */}
                <Col span={10}>
                    <div className="section-title" style={{ marginBottom: 16, fontWeight: 500, fontSize: 16 }}>
                        <GlobalOutlined style={{ marginRight: 8 }} />
                        定价参数配置
                    </div>

                    <PricingParametersCard
                        form={form}
                        categoryFromBasic={state.categoryFromBasic}
                        site={state.site}
                        setSite={state.setSite}
                        exchangeRate={data.exchangeRate}
                        setExchangeRate={data.setExchangeRate}
                        purchaseCostCNY={state.purchaseCostCNY}
                        setPurchaseCostCNY={state.setPurchaseCostCNY}
                        selectedChannelId={state.selectedChannelId}
                        setSelectedChannelId={state.setSelectedChannelId}
                        channels={data.channels}
                        loading={data.loading}
                        selectedChannelRateInfo={data.selectedChannelRateInfo}
                        chargeableWeight={data.chargeableWeight}
                        shippingCostCNY={data.shippingCostCNY}
                        dutyRate={state.dutyRate}
                        setDutyRate={state.setDutyRate}
                        productType={state.productType}
                        setProductType={state.setProductType}
                        referralRate={data.referralRate}
                        setReferralRate={data.setReferralRate}
                        returnRate={state.returnRate}
                        setReturnRate={state.setReturnRate}
                        otherCostRate={state.otherCostRate}
                        setOtherCostRate={state.setOtherCostRate}
                        setLastChanged={state.setLastChanged}
                        fbaFee={data.fbaFee}
                        setFbaFee={data.setFbaFee}
                        fbaTierName={data.fbaTierName}
                        isPeakSeason={state.isPeakSeason}
                        setIsPeakSeason={state.setIsPeakSeason}
                        currentStorageFee={data.currentStorageFee}
                        storageFee={data.storageFee}
                        volumeWeight={data.volumeWeight}
                    />
                </Col>

                {/* 中间：分割线 */}
                <Col span={1} style={{ display: 'flex', justifyContent: 'center' }}>
                    <Divider type="vertical" style={{ height: '100%' }} />
                </Col>

                {/* 右侧：计算结果展示 */}
                <Col span={13}>
                    <div className="section-title" style={{ marginBottom: 16, fontWeight: 500, fontSize: 16 }}>
                        定价与利润分析
                    </div>

                    <PricingResultsPanel
                        landedCost={data.landedCost}
                        fbaFee={data.fbaFee}
                        referralRate={data.referralRate}
                        currentStorageFee={data.currentStorageFee}
                        returnRate={state.returnRate}
                        otherCostRate={state.otherCostRate}
                        channelResults={data.channelResults}
                        selectedChannelId={state.selectedChannelId}
                        channelColumns={getChannelResultColumns(data.bestChannel || null)}
                        bestChannel={data.bestChannel}
                    />
                </Col>
            </Row>

            {/* 下方：广告策略与利润推演模型（全宽） */}
            <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                    <ProfitScenarioAnalysis
                        price={price || 0}
                        profitMarginPreAd={profitMargin || 0}
                        landedCost={data.landedCost}
                    />
                </Col>
            </Row>

            {/* 底部：分阶段运营策略模拟 */}
            <Row style={{ marginTop: 24 }}>
                <Col span={24}>
                    {/* 隐藏字段，用于存储运营策略数据 */}
                    <Form.Item name="operation_strategy_data" hidden>
                        <Input.TextArea />
                    </Form.Item>

                    <OperationStrategySimulation
                        skuId={skuId}
                        landedCost={data.landedCost}
                        fbaFee={data.fbaFee}
                        referralRate={data.referralRate}
                        storageFee={data.currentStorageFee}
                        shippingCost={data.shippingCostUSD}
                        initialData={form.getFieldValue('operation_strategy_data')}
                        onDataChange={(strategyData) => {
                            // 当策略数据变化时，同步到 Form 字段中
                            if (form) {
                                form.setFieldValue('operation_strategy_data', strategyData);
                            }
                        }}
                    />
                </Col>
            </Row>
        </div>
    );
};
